import { Modal } from "react-bootstrap";
import { CustomVisibilityEntities, GoalItem, TGoalForm } from "../types";
import { useCallback, useEffect, useState } from "react";
import Button from "app/storybookComponents/Button";
import { useCreateGoalMutation, useUpdateGoalMutation } from "../goalsApi";
import GoalWizard from "./components/GoalWizard";
import { trackGoalSaved, trackModalOpened } from "utils/trackingFunctions";
import { selectCurrentUserAccountId } from "app/containers/Global/slice";
import { useAppSelector } from "utils/redux/hooks";

interface Props {
  isOpen: boolean;
  hideModal: () => void;
  goal?: GoalItem;
  isOrgGoal?: boolean;
  goalScope?: "org" | "all";
}

const GoalModal = ({
  isOpen,
  hideModal,
  goal,
  isOrgGoal,
  goalScope,
}: Readonly<Props>) => {
  // -------------- API Mutations --------------
  const [updateGoal, { isLoading: isUpdating }] = useUpdateGoalMutation();
  const [createGoal, { isLoading: isCreating }] = useCreateGoalMutation();

  // -------------- State --------------
  const [wizardStep, setWizardStep] = useState(1);
  const [form, setForm] = useState<TGoalForm>({
    title: "",
    successCriteria: "",
    dueDate: "",
    parentGoalId: null,
    type: "",
    visibility: "standard",
    parentGoalTitle: "",
    is_org_goal: isOrgGoal ?? false,
  });
  const [customVisibilityEntities, setCustomVisibilityEntities] =
    useState<CustomVisibilityEntities>({
      userAccountIds: [...(goal?.customAllowedUserIds ?? [])],
      teamIds: [...(goal?.customAllowedTeamIds ?? [])],
      departmentIds: [...(goal?.customAllowedDepartmentIds ?? [])],
    });
  const currentUserAccountId = useAppSelector(selectCurrentUserAccountId);

  // ---------------------------- Helpers ----------------------------

  const onResetForm = useCallback(() => {
    setForm({
      title: goal?.title ?? "",
      successCriteria: goal?.successCriteria ?? "",
      dueDate: goal?.dueDate?.substring(0, 10) ?? "",
      parentGoalId: goal?.parentGoalId ?? null,
      type: goal?.type ?? "",
      visibility: goal?.visibility ?? "standard",
      parentGoalTitle: goal?.parentGoal?.title ?? "",
      is_org_goal: isOrgGoal ?? false,
    });
    setCustomVisibilityEntities({
      userAccountIds: [...(goal?.customAllowedUserIds ?? [])],
      teamIds: [...(goal?.customAllowedTeamIds ?? [])],
      departmentIds: [...(goal?.customAllowedDepartmentIds ?? [])],
    });
    setWizardStep(1);
  }, [goal, isOrgGoal]);

  // ---------------------------- Effects ----------------------------

  // Reset form when modal opens or goal changes
  useEffect(() => {
    if (isOpen) {
      onResetForm();
    }
  }, [isOpen, goal?.goalId, onResetForm]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    trackModalOpened("Goals");
  }, [isOpen]);

  // ---------------------------- Handlers ----------------------------

  const resetModal = () => {
    onResetForm();
    hideModal();
  };

  const onSaveNewGoal = async () => {
    if (isCreating) return;
    await createGoal({
      ...form,
      is_org_goal: !!isOrgGoalToSend,
      customAllowedUserIds: customVisibilityEntities.userAccountIds,
      customAllowedTeamIds: customVisibilityEntities.teamIds,
      customAllowedDepartmentIds: customVisibilityEntities.departmentIds,
    }).unwrap();
    trackGoalSaved({ isNewGoal: true });
    resetModal();
  };

  const onSaveEditGoal = async () => {
    if (!goal || isUpdating) return;
    await updateGoal({
      goalId: goal.goalId,
      payload: {
        title: form.title,
        successCriteria: form.successCriteria,
        dueDate: form.dueDate,
        type: form.type,
        visibility: form.visibility,
        parentGoalId:
          typeof form.parentGoalId === "number" && form.parentGoalId > 0
            ? form.parentGoalId
            : null,
        is_org_goal: !!isOrgGoal,
        customAllowedUserIds: customVisibilityEntities.userAccountIds,
        customAllowedTeamIds: customVisibilityEntities.teamIds,
        customAllowedDepartmentIds: customVisibilityEntities.departmentIds,
      },
    });
    trackGoalSaved({ isNewGoal: false });
    resetModal();
  };

  const onSave = () => {
    if (goal) {
      return onSaveEditGoal();
    }
    onSaveNewGoal();
  };

  // ---------------------------- Derived State/Props ----------------------------
  const isOrgGoalToSend = isOrgGoal && goalScope === "org";
  const isSubmitting = goal ? isUpdating : isCreating;

  return (
    <Modal
      show={isOpen}
      onHide={resetModal}
      centered
      size="lg"
      backdrop="static"
      className="figtree-container"
    >
      <div className="goal-modal">
        {/* HEADER */}
        <div className="gm-header p-lg pb-0">
          <div>
            <h2>{goal ? "Edit" : "Set a"} Goal</h2>
          </div>
          <Button
            onClick={hideModal}
            variant="secondary-blue"
            xIcon
            className="border-0"
          />
        </div>
        <GoalWizard
          onBack={hideModal}
          onSave={onSave}
          isOrgGoal={isOrgGoal}
          isLoading={isSubmitting}
          form={form}
          onFormChange={setForm}
          customVisibilityEntities={customVisibilityEntities}
          onChangeCustomVisibilityEntities={setCustomVisibilityEntities}
          wizardStep={wizardStep}
          onWizardStepChange={setWizardStep}
          saveGoalButtonText={goal ? "Update Goal" : "Next"}
          selectedUserId={currentUserAccountId}
        />
      </div>
    </Modal>
  );
};

export default GoalModal;
